import { useEffect, useState, useCallback, useMemo } from 'react';
import { debounce } from 'lodash';
import { useSelector } from 'react-redux';
import { Button } from '../../../components/ui/button';
import DataTable from '../../../components/ui/data-table/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { SearchInput } from '../../../components/ui/search-input';
import staticBannerService from '../../../services/StaticBannerService';
import highlightOrderService from 'services/HighlightOrderService';
import { FormDialog } from '../../../components/ui/form-dialog';
import CommonFormNew from '../../../components/commonFormNew/commonFormNew';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { dateFormate } from 'utils/utils';
import Edit2Icon from 'lucide-react/dist/esm/icons/edit-2';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import PlusIcon from 'lucide-react/dist/esm/icons/plus';
import X from 'lucide-react/dist/esm/icons/x';
import MoreVertical from 'lucide-react/dist/esm/icons/more-vertical';
import { toast, ToastContainer } from 'react-toastify';
import { NewPagination } from '../../../components/ui/newPagination';
import dayjs from 'dayjs';

export default function StaticBanner() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isAddBannerOpen, setIsAddBannerOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingBannerId, setEditingBannerId] = useState(null);
  const [addToHighlightBannerId, setAddToHighlightBannerId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    image: null,
    dateRange: { startDate: null, endDate: null },
    city: null,
    duration: { startDate: null, endDate: null }, // Added for highlight form
    location: null, // Added for highlight form
  });
  const [selectedImage, setSelectedImage] = useState(null);

  const rawCities = useSelector((state) => state?.locationDropdown?.locations || []);

  const cityOptions = [{ id: 'all', name: 'All City' }, ...rawCities];

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Fixed form validation function
  function isFormValid(form, fields) {
    return fields.every((field) => {
      if (!field.required) return true;

      const fieldValue = form[field.name];

      switch (field.type) {
        case 'input':
          return fieldValue && fieldValue.trim() !== '';
        case 'image':
          return fieldValue !== null && fieldValue !== undefined;
        case 'dateRange':
          return (
            fieldValue &&
            fieldValue.startDate &&
            fieldValue.endDate &&
            (Array.isArray(form?.city) ? form?.city.length > 0 : form?.city !== null)
          );
        default:
          return fieldValue !== null && fieldValue !== undefined && fieldValue !== '';
      }
    });
  }

  const fetchBanners = useCallback(
    async (search, page, limit) => {
      try {
        let query = {};
        if (search) {
          query.search = encodeURIComponent(search.trim());
        }
        query.page = page || 1;
        query.limit = limit || 10;
        const res = await staticBannerService.getBannerList(query);
        const headers = res?.data?.header || [];
        const body = res?.data?.body || [];
        setTotalItems(res?.data?.totalCount || 0);

        const dynamicColumns = headers.map(({ key, label }) => {
          if (key === 'image') {
            return {
              accessorKey: key,
              header: label,
              cell: ({ row }) => {
                const img = row.original.image;
                return (
                  <div onClick={(e) => e.stopPropagation()}>
                    {img && img.url ? (
                      <img
                        src={img.url}
                        alt={img.name}
                        style={{
                          width: 60,
                          height: 40,
                          objectFit: 'cover',
                          borderRadius: 4,
                          cursor: 'pointer',
                        }}
                        onClick={() => setSelectedImage(img.url)}
                      />
                    ) : (
                      <span className="text-gray-400">No Image</span>
                    )}
                  </div>
                );
              },
            };
          }
          return {
            accessorKey: key,
            header: label,
          };
        });

        const actionsColumn = {
          accessorKey: 'actions',
          header: 'Action',
          cell: ({ row }) => (
            <div className="flex justify-end pr-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 cursor-pointer hover:bg-gray-100 focus:outline-none focus:ring-0"
                  >
                    <span className="sr-only">Open menu</span>
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  side="bottom"
                  sideOffset={0}
                  className="bg-white border border-gray-200 shadow-lg rounded-[8px] min-w-[120px] p-1"
                >
                  <DropdownMenuItem
                    className="cursor-pointer hover:bg-gray-50 focus:bg-gray-50 px-3 py-2 text-sm text-gray-700 rounded-md transition-colors duration-150 focus:outline-none"
                    style={{
                      letterSpacing: '0.00938em',
                      fontSize: '16px',
                      fontWeight: 500,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setAddToHighlightBannerId(row.original.id);
                      setFormData({
                        title: '',
                        image: null,
                        dateRange: { startDate: null, endDate: null },
                        city: null,
                        duration: { startDate: null, endDate: null },
                        location: null,
                      });
                      setIsAddBannerOpen(true);
                    }}
                  >
                    <span className="flex items-center">
                      <PlusIcon className="h-4 w-4" />
                    </span>
                    Add to highlight
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer hover:bg-gray-50 focus:bg-gray-50 px-3 py-2 text-sm text-gray-700 rounded-md transition-colors duration-150 focus:outline-none"
                    style={{
                      letterSpacing: '0.00938em',
                      fontSize: '16px',
                      fontWeight: 500,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setFormData({
                        title: row.original.title,
                        image: row.original.image,
                        dateRange: row.original.dateRange || { startDate: null, endDate: null },
                        city: row.original.city || null,
                        duration: { startDate: null, endDate: null },
                        location: null,
                      });
                      setEditingBannerId(row.original.id);
                      setIsEditing(true);
                      setIsAddBannerOpen(true);
                    }}
                  >
                    <span className="flex items-center">
                      <Edit2Icon className="h-4 w-4" />
                    </span>
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer hover:bg-gray-50 focus:bg-gray-50 px-3 py-2 text-sm text-gray-700 rounded-md transition-colors duration-150 focus:outline-none"
                    style={{
                      letterSpacing: '0.00938em',
                      fontSize: '16px',
                      fontWeight: 500,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await staticBannerService.deleteBanner(row.original.id);
                        toast.success(`Banner '${row.original.title}' deleted successfully`);
                        fetchBanners(searchQuery);
                      } catch (error) {
                        toast.error('Failed to delete banner');
                      }
                    }}
                  >
                    <span className="flex items-center">
                      <Trash2 className="h-4 w-4" />
                    </span>
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ),
        };

        setColumns([...dynamicColumns, actionsColumn]);
        setFilteredData(body);
      } catch (error) {
        console.error('Error fetching banners:', error);
      }
    },
    [searchQuery],
  );

  const handleAddBanner = async () => {
    try {
      const payload = {
        title: formData.title,
        bannerImage: formData.image,
        city: formData.city,
        isB2B: true,
        dateRange: {
          startDate: formData.dateRange?.startDate
            ? dateFormate(formData.dateRange.startDate, 'MM/DD/YYYY')
            : '',
          endDate: formData.dateRange?.endDate
            ? dateFormate(formData.dateRange.endDate, 'MM/DD/YYYY')
            : '',
        },
      };

      if (isEditing && editingBannerId) {
        await staticBannerService.updateBanner(editingBannerId, payload);
        toast.success('Banner updated successfully');
      } else {
        await staticBannerService.createBanner(payload);
        toast.success('Banner added successfully');
      }

      setFormData({
        title: '',
        image: null,
        dateRange: { startDate: null, endDate: null },
        city: null,
        duration: { startDate: null, endDate: null },
        location: null,
      });
      setIsEditing(false);
      setEditingBannerId(null);
      setIsAddBannerOpen(false);
      fetchBanners();
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          (isEditing ? 'Failed to update banner' : 'Failed to add banner'),
      );
    }
  };

  const handleAddToHighLight = async () => {
    try {
      const payload = {
        type: 'banner',
        referenceId: addToHighlightBannerId,
        locations: Array.isArray(formData.city)
          ? formData.city.map((itm) => itm.id)
          : [formData.city?.id].filter(Boolean),
        startDate: formData.dateRange?.startDate
          ? dateFormate(formData.dateRange.startDate, 'toIsoString')
          : '',
        endDate: formData.dateRange?.endDate
          ? dateFormate(formData.dateRange.endDate, 'toIsoString')
          : '',
        isB2B: true,
      };

      await highlightOrderService.addToHighLight(payload);
      toast.success('Banner added to highlight successfully');

      setFormData({
        title: '',
        image: null,
        dateRange: { startDate: null, endDate: null },
        city: null,
        duration: { startDate: null, endDate: null },
        location: null,
      });
      setIsEditing(false);
      setEditingBannerId(null);
      setIsAddBannerOpen(false);
      setAddToHighlightBannerId(null);
      fetchBanners();
    } catch (error) {
      console.log('err', error, error.response?.data);
      toast.error(error.response?.data?.message || 'Failed to add banner to highlight');
    }
  };

  const handleCancel = () => {
    setFormData({
      title: '',
      image: null,
      dateRange: { startDate: null, endDate: null },
      city: null,
      duration: { startDate: null, endDate: null },
      location: null,
    });
    setIsEditing(false);
    setEditingBannerId(null);
    setIsAddBannerOpen(false);
    setAddToHighlightBannerId(null);
  };

  // Debounced search
  const debouncedFetchBanners = useMemo(
    () =>
      debounce((query) => {
        fetchBanners(query);
      }, 500),
    [fetchBanners],
  );

  const handleSearch = (query) => {
    setSearchQuery(query || '');
    debouncedFetchBanners(query);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchBanners(null, page, itemsPerPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    fetchBanners(null, 1, newItemsPerPage);
  };

  useEffect(() => {
    // Call fetchBanners with current page and items per page
    fetchBanners(searchQuery, currentPage, itemsPerPage);
  }, [currentPage, itemsPerPage, searchQuery, fetchBanners]);

  useEffect(() => {
    return () => {
      debouncedFetchBanners.cancel();
    };
  }, [debouncedFetchBanners]);

  // Updated field configurations
  const bannerFields = [
    { name: 'title', label: 'Name', type: 'input', required: true, placeholder: 'Enter Name' },
    { name: 'image', label: '', type: 'image', required: true },
  ];

  const addToHighlightFields = [
    {
      name: 'dateRange',
      label: 'Duration',
      type: 'dateRange',
      required: true,
      placeholder: 'Duration',
      minDate: dayjs().add(1, 'day'),
    },
    { name: 'city', label: 'City', type: 'city', required: true, placeholder: 'City' },
  ];

  // Determine current fields and form validity
  const currentFields = addToHighlightBannerId ? addToHighlightFields : bannerFields;
  const isValid = isFormValid(formData, currentFields);

  return (
    <div className="mx-auto">
      <div className="mb-4 flex justify-end">
        <div className="flex items-center gap-2">
          <SearchInput onSearch={handleSearch} placeholder={'Banner Name'} className="w-[280px]" />

          <FormDialog
            trigger={
              <Button className="h-[40px] bg-[color:var(--brand-accent)] hover:bg-[color:var(--brand-accent)] rounded-[5px] font-normal text-[14px] text-white border border-white capitalize">
                + Add Banner
              </Button>
            }
            title={isEditing ? 'Edit' : 'Add'}
            subtitle={addToHighlightBannerId ? 'To Highlight' : 'Banner'}
            isOpen={isAddBannerOpen}
            onOpenChange={setIsAddBannerOpen}
            onSubmit={addToHighlightBannerId ? handleAddToHighLight : handleAddBanner}
            onCancel={handleCancel}
            submitLabel={isEditing ? 'Update' : 'Create'}
            submitDisabled={!isValid}
            hideCancel={true}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              {addToHighlightBannerId && (
                <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-2">
                  Highlights must be configured at least one day in advance. Please select tomorrow
                  or a later date.
                </p>
              )}
              <CommonFormNew
                config={currentFields}
                formData={formData}
                onFormDataChange={setFormData}
                cityOptions={cityOptions}
              />
            </LocalizationProvider>
          </FormDialog>
        </div>
      </div>
      <div>
        <DataTable
          columns={columns}
          data={filteredData}
          onRowClick={(row) =>
            toast({ title: 'Row Clicked', description: `You clicked ${row.toll}` })
          }
          pageSize={itemsPerPage}
        />
        <NewPagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </div>
      <ToastContainer />

      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="relative bg-white p-4 rounded-lg w-[800px] h-[600px] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage}
              alt="Preview"
              className="max-w-full max-h-full object-contain"
            />
            <button
              className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-md hover:bg-gray-100"
              onClick={() => setSelectedImage(null)}
            >
              <X size={24} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
